/**
 * Insights: the four questions, one screen each, reachable from More.
 *
 * Every one of these leads with the WINDOW it measured over. A dormancy list
 * that does not say "measured over 129 days of history" invites the reader to
 * assume it means "in living memory", and act on it.
 */
import { useMemo } from "react";

import { ShareBar } from "../components/ShareBar.js";
import { Card, Empty, Money, Row, Rows, SectionTitle } from "../components/ui.js";
import {
  deriveDeadStock,
  deriveDormant,
  deriveTopCustomers,
  deriveTopItems,
} from "../derive/insights.js";
import type { Window } from "../derive/insights.js";
import { formatDate, formatMoney, formatQty } from "../derive/reports.js";
import { navigate } from "../routes.js";
import { useApp } from "../state.js";

function WindowNote({ window, extra }: { window: Window; extra?: string }) {
  if (!window.from) return null;
  return (
    <p className="px-1 text-xs leading-relaxed text-ink-3">
      Measured over {window.days} days of history — {formatDate(window.from)} to{" "}
      {formatDate(window.to)}. {extra}
    </p>
  );
}

/* ───────────────────────────────────────────────────── index ── */

const ITEMS = [
  { id: "dormant", title: "Customers who stopped buying", sub: "worth a phone call" },
  { id: "top-customers", title: "Top customers", sub: "who your revenue depends on" },
  { id: "top-items", title: "Top items", sub: "what actually sells" },
  { id: "dead-stock", title: "Not selling", sub: "capital sitting on the shelf" },
] as const;

export function InsightsIndex() {
  const { snapshot, groups, asOf } = useApp();

  const counts = useMemo(() => {
    if (!snapshot || !groups) return null;
    return {
      dormant: deriveDormant(snapshot, asOf, groups).customers.length,
      dead: deriveDeadStock(snapshot).items.length,
    };
  }, [snapshot, groups, asOf]);

  if (!snapshot) return <Empty>Nothing loaded.</Empty>;

  return (
    <Card title="Insights" flush>
      <Rows>
        {ITEMS.map((item) => (
          <Row
            key={item.id}
            title={item.title}
            sub={item.sub}
            right={
              item.id === "dormant" && counts?.dormant
                ? String(counts.dormant)
                : item.id === "dead-stock" && counts?.dead
                  ? String(counts.dead)
                  : undefined
            }
            onClick={() => navigate({ id: item.id })}
          />
        ))}
      </Rows>
    </Card>
  );
}

/* ─────────────────────────────────────────── 1 · dormant ── */

export function Dormant() {
  const { snapshot, groups, asOf } = useApp();
  const report = useMemo(
    () => (snapshot && groups ? deriveDormant(snapshot, asOf, groups) : null),
    [snapshot, groups, asOf],
  );

  if (!report) return <Empty>Nothing loaded.</Empty>;

  if (report.windowTooShort) {
    // Refuse rather than mislead: with less history than the threshold, every
    // customer who bought early looks dormant and the list is meaningless.
    return (
      <Empty>
        This snapshot holds {report.window.days} days of history, which is less than the{" "}
        {report.threshold}-day threshold. There is not enough to say who has stopped buying.
      </Empty>
    );
  }

  return (
    <div className="space-y-4">
      <WindowNote
        window={report.window}
        extra={`A customer counts as dormant after ${report.threshold} days with no invoice.`}
      />

      <Card>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[30px] font-semibold leading-none tracking-tight text-ink">
            {report.customers.length}
          </span>
          <span className="text-xs text-ink-3">
            of {report.activeCount} customers who have ever bought
          </span>
        </div>
        {report.customers.length > 0 && (
          <p className="mt-3 text-sm text-ink-2">
            Together they bought{" "}
            <strong className="text-ink">
              {formatMoney(report.customers.reduce((t, c) => t + c.lifetimeValue, 0))}
            </strong>{" "}
            in this period, and {report.customers.filter((c) => c.balance > 0.01).length} still owe
            money.
          </p>
        )}
      </Card>

      {report.customers.length === 0 ? (
        <Empty>Every customer has bought within the last {report.threshold} days.</Empty>
      ) : (
        <>
          <ShareBar
            csv={[
              {
                label: "Export CSV",
                filename: "dormant-customers",
                build: () => [
                  ["Customer", "Last sale", "Days since", "Bought in period", "Invoices", "Still owed"],
                  ...report.customers.map((c) => [
                    c.ledger,
                    c.lastSale,
                    c.daysSince,
                    c.lifetimeValue.toFixed(2),
                    c.invoices,
                    c.balance.toFixed(2),
                  ]),
                ],
              },
            ]}
          />

          <div>
            <SectionTitle>Biggest first — they are the calls that pay</SectionTitle>
            <Card flush>
              <Rows>
                {report.customers.map((c) => (
                  <Row
                    key={c.ledger}
                    avatar
                    title={c.ledger}
                    sub={`last bought ${formatDate(c.lastSale)} · ${c.daysSince} days ago · ${c.invoices} ${
                      c.invoices === 1 ? "invoice" : "invoices"
                    }`}
                    right={<Money amount={c.lifetimeValue} />}
                    rightSub={c.balance > 0.01 ? `owes ${formatMoney(c.balance)}` : undefined}
                    onClick={() => navigate({ id: "ledger", param: c.ledger })}
                  />
                ))}
              </Rows>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────── 2 · top customers ── */

export function TopCustomers() {
  const { snapshot, groups } = useApp();
  const report = useMemo(
    () => (snapshot && groups ? deriveTopCustomers(snapshot, groups, 20) : null),
    [snapshot, groups],
  );

  if (!report) return <Empty>Nothing loaded.</Empty>;
  if (report.rows.length === 0) return <Empty>No sales in this snapshot.</Empty>;

  const topFive = report.rows.slice(0, 5).reduce((t, r) => t + r.share, 0);

  return (
    <div className="space-y-4">
      <WindowNote window={report.window} />

      <Card>
        <p className="text-sm text-ink-2">
          The top five customers are{" "}
          <strong className="text-ink">{topFive.toFixed(0)}%</strong> of{" "}
          {formatMoney(report.total)} in sales.
          {topFive > 60 && " That is a lot of weight on a few relationships."}
        </p>
      </Card>

      <ShareBar
        csv={[
          {
            label: "Export CSV",
            filename: "top-customers",
            build: () => [
              ["Customer", "Sales", "Invoices", "Share %"],
              ...report.rows.map((r) => [r.ledger, r.value.toFixed(2), r.invoices, r.share.toFixed(1)]),
            ],
          },
        ]}
      />

      <Card flush>
        <Rows>
          {report.rows.map((r) => (
            <Row
              key={r.ledger}
              avatar
              title={r.ledger}
              sub={`${r.invoices} ${r.invoices === 1 ? "invoice" : "invoices"} · ${r.share.toFixed(1)}%`}
              right={<Money amount={r.value} />}
              onClick={() => navigate({ id: "ledger", param: r.ledger })}
            />
          ))}
        </Rows>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────── 3 · top items ── */

export function TopItems() {
  const { snapshot } = useApp();
  const report = useMemo(() => (snapshot ? deriveTopItems(snapshot, 20) : null), [snapshot]);

  if (!report) return <Empty>Nothing loaded.</Empty>;
  if (report.rows.length === 0) return <Empty>No item lines on the sales in this snapshot.</Empty>;

  return (
    <div className="space-y-4">
      <WindowNote window={report.window} />

      <ShareBar
        csv={[
          {
            label: "Export CSV",
            filename: "top-items",
            build: () => [
              ["Item", "Quantity", "Unit", "Value", "Share %"],
              ...report.rows.map((r) => [r.item, r.qty, r.unit, r.value.toFixed(2), r.share.toFixed(1)]),
            ],
          },
        ]}
      />

      <Card title={`${formatMoney(report.total)} across all items`} flush>
        <Rows>
          {report.rows.map((r) => (
            <Row
              key={r.item}
              title={r.item}
              sub={`${formatQty(r.qty, r.unit)} · ${r.share.toFixed(1)}%`}
              right={<Money amount={r.value} />}
            />
          ))}
        </Rows>
      </Card>
    </div>
  );
}

/* ────────────────────────────────────────── 4 · dead stock ── */

export function DeadStock() {
  const { snapshot } = useApp();
  const report = useMemo(() => (snapshot ? deriveDeadStock(snapshot) : null), [snapshot]);

  if (!report) return <Empty>Nothing loaded.</Empty>;

  return (
    <div className="space-y-4">
      <WindowNote
        window={report.window}
        extra="Counts sales only — a stock journal moving an item between godowns is not a sale."
      />

      {report.items.length === 0 ? (
        <Empty>Every item you hold has sold at least once in this period.</Empty>
      ) : (
        <>
          <Card>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[30px] font-semibold leading-none tracking-tight text-ink">
                {report.items.length}
              </span>
              <span className="text-xs text-ink-3">items held, none sold</span>
            </div>
            {report.valuesWithheld ? (
              <p className="mt-3 text-sm text-warn">
                Stock values are not part of this view, so the capital tied up cannot be shown.
              </p>
            ) : (
              <p className="mt-3 text-sm text-ink-2">
                <strong className="text-ink">{formatMoney(report.valueLockedUp)}</strong> of stock
                that has not sold in this period.
              </p>
            )}
          </Card>

          <ShareBar
            csv={[
              {
                label: "Export CSV",
                filename: "not-selling",
                build: () => [
                  ["Item", "Quantity", "Unit", "Value"],
                  ...report.items.map((i) => [
                    i.item,
                    i.closingQty,
                    i.unit,
                    i.closingValue.toFixed(2),
                  ]),
                ],
              },
            ]}
          />

          <Card flush>
            <Rows>
              {report.items.slice(0, 200).map((i) => (
                <Row
                  key={i.item}
                  title={i.item}
                  sub={formatQty(i.closingQty, i.unit)}
                  right={report.valuesWithheld ? undefined : <Money amount={i.closingValue} />}
                />
              ))}
            </Rows>
          </Card>
        </>
      )}
    </div>
  );
}
